import axios from 'axios';
import { INTEGRATIONS_API_URL } from '../components/common/constants';

// ---------------------------------------------------------------- #
// Integrations API client
// ---------------------------------------------------------------- #
//
// One exported function per endpoint of the parki-api integrations module.
// Shapes are in plan/2026-10-30/14-endpoint-shapes.md.
//
// Every function returns the whole response envelope
// ({ success, message, data }), the same as api_dashboard.js, so callers
// read `result.data`. On an error it throws the envelope the API sent back
// (so `error.message` is plain English the page can show), or the original
// axios error when the API could not be reached.
//
// Tokens never pass through here except the user's own Cognito token.

// ---------------------------------------------------------------- #
// Request helpers
// ---------------------------------------------------------------- #

/**
 * Build the request headers, carrying the logged-in user's Cognito token.
 */
const buildHeaders = () => {
  // Read the token the login stored
  const token = localStorage.getItem('authToken');

  // Send it as a bearer token
  return {
    'Authorization': `Bearer ${token}`
  };
};

/**
 * Turn an axios error into the error the page shows: the API's envelope when it sent one.
 */
const toPageError = (error) => {
  // Use the API's own envelope when there is one, so `message` is plain English
  const apiEnvelope = error.response?.data;
  const hasEnvelope = apiEnvelope && typeof apiEnvelope === 'object';
  if (hasEnvelope) {
    // Keep the HTTP status next to the envelope so the page can spot a 401
    apiEnvelope.httpStatus = error.response.status;
    return apiEnvelope;
  }

  // Otherwise hand back the original error (network down, CORS and so on)
  return error;
};

/**
 * GET an integrations endpoint with optional query parameters.
 */
const integrationsGet = async (path, params = {}) => {
  try {
    // Call the endpoint with the bearer token
    const response = await axios.get(`${INTEGRATIONS_API_URL}/${path}`, {
      headers: buildHeaders(),
      params: params
    });

    // Hand back the envelope
    return response.data;
  } catch (error) {
    // Log the path only; never the response body, which may hold provider details
    console.error(`Error calling integrations GET ${path}`);
    throw toPageError(error);
  }
};

/**
 * POST a JSON body to an integrations endpoint.
 */
const integrationsPost = async (path, body = {}) => {
  try {
    // Call the endpoint with the bearer token and a JSON body
    const response = await axios.post(`${INTEGRATIONS_API_URL}/${path}`, body, {
      headers: buildHeaders()
    });

    // Hand back the envelope
    return response.data;
  } catch (error) {
    // Log the path only; never the response body, which may hold provider details
    console.error(`Error calling integrations POST ${path}`);
    throw toPageError(error);
  }
};

// ---------------------------------------------------------------- #
// Register, summary and exclusions
// ---------------------------------------------------------------- #

// Get the parks by products grid
export const getRegister = async () => {
  return integrationsGet('register');
};

// Get the counts for the sidebar widget
export const getSummary = async () => {
  return integrationsGet('summary');
};

// Mark a product not needed for one park, or needed again
export const setExclusion = async (parkiId, product, notNeeded) => {
  return integrationsPost('exclusions/set', {
    parkiId: parkiId,
    product: product,
    notNeeded: notNeeded
  });
};

// ---------------------------------------------------------------- #
// OAuth
// ---------------------------------------------------------------- #

// Start a provider sign-in; the response carries the authoriseUrl to send the browser to
export const startOAuth = async (provider, parkiId = null) => {
  // Only send the park when the wizard was opened from one
  const body = { provider: provider };
  if (parkiId !== null && parkiId !== undefined) {
    body.parkiId = parkiId;
  }

  return integrationsPost('oauth/start', body);
};

// ---------------------------------------------------------------- #
// PMS: RMS and Newbook
// ---------------------------------------------------------------- #

// Send (or with dryRun, preview) the PMS access request email
export const sendPmsRequest = async (parkiId, application, note = '', dryRun = false) => {
  return integrationsPost('pms/send-request', {
    parkiId: parkiId,
    application: application,
    note: note,
    dryRun: dryRun
  });
};

// ---------------------------------------------------------------- #
// Xero
// ---------------------------------------------------------------- #

// Get the entity's Xero organisations with their park links
export const getXeroTenants = async () => {
  return integrationsGet('xero/tenants');
};

// Get the tracking categories, options and budgets for one Xero organisation
export const getXeroTenantOptions = async (bdoXeroId) => {
  return integrationsGet('xero/tenant-options', { bdoXeroId: bdoXeroId });
};

// Save every park linked to one Xero organisation; parks left out are unlinked
export const saveXeroTenantMapping = async (bdoXeroId, parks) => {
  return integrationsPost('xero/save-tenant-mapping', {
    bdoXeroId: bdoXeroId,
    parks: parks
  });
};

// Disconnect one Xero organisation and unlink its parks
export const disconnectXeroTenant = async (bdoXeroId) => {
  return integrationsPost('xero/disconnect-tenant', { bdoXeroId: bdoXeroId });
};

// ---------------------------------------------------------------- #
// MYOB
// ---------------------------------------------------------------- #

// Get the entity's MYOB company files with their park links
export const getMyobFiles = async () => {
  return integrationsGet('myob/files');
};

// Get the jobs for one MYOB company file
export const getMyobFileJobs = async (myobId) => {
  return integrationsGet('myob/file-jobs', { myobId: myobId });
};

// Save every park linked to one MYOB company file; parks left out are unlinked
export const saveMyobFileMapping = async (myobId, parks) => {
  return integrationsPost('myob/save-file-mapping', {
    myobId: myobId,
    parks: parks
  });
};

// ---------------------------------------------------------------- #
// Google
// ---------------------------------------------------------------- #

// Get the entity's Google connection and its cached accounts
export const getGoogleAccounts = async () => {
  return integrationsGet('google/accounts');
};

// List the Google accounts again from Google
export const refreshGoogleAccounts = async () => {
  return integrationsPost('google/refresh-accounts', {});
};

// Save the Analytics property and Business location for the given parks
export const saveGoogleMapping = async (parks) => {
  return integrationsPost('google/save-mapping', { parks: parks });
};

// Disconnect Google for the whole entity
export const disconnectGoogle = async () => {
  return integrationsPost('google/disconnect', {});
};

// ---------------------------------------------------------------- #
// Meta: Facebook and Instagram
// ---------------------------------------------------------------- #

// Get the entity's Meta connections with their cached pages and Instagram accounts
export const getMetaAccounts = async () => {
  return integrationsGet('meta/accounts');
};

// List one Meta connection's pages and Instagram accounts again from Facebook
export const refreshMetaAccounts = async (connectionId) => {
  return integrationsPost('meta/refresh-accounts', { connectionId: connectionId });
};

// Save the Facebook page and Instagram account for the given parks
export const saveMetaMapping = async (parks) => {
  return integrationsPost('meta/save-mapping', { parks: parks });
};

// Disconnect one Meta connection
export const disconnectMeta = async (connectionId) => {
  return integrationsPost('meta/disconnect', { connectionId: connectionId });
};

// Delete the stored Meta data for the given parks; confirm must be the typed text "DELETE"
export const deleteMetaData = async (parkiIds, confirm) => {
  return integrationsPost('meta/delete-data', {
    parkiIds: parkiIds,
    confirm: confirm
  });
};

// ---------------------------------------------------------------- #
// Xero re-authorisation (remove with xero-reauth)
// ---------------------------------------------------------------- #

// Get the Xero organisations still to re-authorise, and whether this session may start one
export const getXeroReauthTenants = async () => {
  return integrationsGet('xero-reauth/tenants');
};

// Start a re-authorisation; the response carries the authoriseUrl to send the browser to
export const startXeroReauth = async (bdoXeroId) => {
  return integrationsPost('xero-reauth/start', { bdoXeroId });
};

// ---------------------------------------------------------------- #
// Default export
// ---------------------------------------------------------------- #

const integrationsApi = {
  getRegister,
  getSummary,
  setExclusion,
  startOAuth,
  sendPmsRequest,
  getXeroTenants,
  getXeroTenantOptions,
  saveXeroTenantMapping,
  disconnectXeroTenant,
  getMyobFiles,
  getMyobFileJobs,
  saveMyobFileMapping,
  getGoogleAccounts,
  refreshGoogleAccounts,
  saveGoogleMapping,
  disconnectGoogle,
  getMetaAccounts,
  refreshMetaAccounts,
  saveMetaMapping,
  disconnectMeta,
  deleteMetaData,
  getXeroReauthTenants, // remove with xero-reauth
  startXeroReauth // remove with xero-reauth
};

export default integrationsApi;
